import { Fragment, useState } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useCdrs } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { formatDuration, formatMoney, formatDateTime, timezoneAbbr } from '../../lib/format.js';
import { CallDetail } from './CallDetail.js';

/**
 * Direction as a glyph with its word beside it for assistive tech.
 *
 * Read by shape down a column, which three same-length words in one colour do
 * not allow. The tone is kept from the old badge so nobody relearns the colours.
 */
const DIRECTION: Record<string, { icon: IconName; label: string; tone: string }> = {
  inbound: { icon: 'callIn', label: 'Inbound', tone: 'var(--arc-ok)' },
  outbound: { icon: 'callOut', label: 'Outbound', tone: 'var(--arc-info)' },
  internal: { icon: 'callInternal', label: 'Internal', tone: 'var(--arc-fg-4)' },
};

export function CdrsPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;
  const session = useSession((s) => s.session);
  const [direction, setDirection] = useState('');
  // One open at a time: the detail is tall, and two open push the list apart.
  const [open, setOpen] = useState<string | null>(null);

  // Tenant timezone, never the browser's -- a call at 09:00 in Windhoek must
  // not read as 10:00 because the admin is in Johannesburg (doc 07 §3).
  const tz = session?.memberships[0]?.tenant_id ? 'Africa/Johannesburg' : 'Africa/Johannesburg';

  const { data, isLoading, error } = useCdrs(tenantId, {
    direction: direction || undefined, limit: 100,
  });

  if (!tenantId) {
    return <Empty icon="accounts" title="No tenant selected" hint="Choose a tenant to view its call history." />;
  }

  return (
    <>
      <div className="arc-page-head">
        <div>
          {!embedded && <h1 className="arc-h1">Call history</h1>}
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Last 30 days · times in {timezoneAbbr(tz)}
          </p>
        </div>
      </div>

      {error && <ErrorBanner error={error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <select
            className="arc-select" style={{ width: 'auto' }}
            value={direction} onChange={(e) => setDirection(e.target.value)}
            aria-label="Filter by direction"
          >
            <option value="">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </select>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {data ? `${data.data.length} calls` : ''}
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} aria-label="Details" />
                <th>Started</th>
                <th aria-label="Direction" style={{ width: 36 }} />
                <th>From</th>
                <th>To</th>
                <th className="arc-td-right">Duration</th>
                <th>Quality</th>
                <th className="arc-td-right">Cost</th>
                {/* Recordings have no page of their own: a recording is reached from
                    its call. The column only says one exists; it plays inside the
                    call, where what it is a recording OF is on screen beside it. */}
                <th aria-label="Recording" style={{ width: 36 }} />
              </tr>
            </thead>
            {isLoading ? (
              <TableSkeleton cols={9} />
            ) : (
              <tbody>
                {data?.data.map((c) => {
                  const d = DIRECTION[c.direction] ?? DIRECTION['internal']!;
                  const isOpen = open === c.id;
                  const toggle = () => setOpen(isOpen ? null : c.id);
                  return (
                  <Fragment key={c.id}>
                  {/* The whole row opens the call; the chevron is the same action
                      for the keyboard, with the state a screen reader announces. */}
                  <tr onClick={toggle} style={{ cursor: 'pointer' }}>
                    <td>
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                        aria-expanded={isOpen} aria-controls={`call-${c.id}`}
                        aria-label={isOpen ? 'Close call details' : 'Open call details'}
                        onClick={(e) => { e.stopPropagation(); toggle(); }}>
                        <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={13} />
                      </button>
                    </td>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(c.started_at, tz)}
                    </td>
                    <td title={d.label}>
                      <span style={{ color: d.tone, display: 'inline-flex' }}>
                        <Icon name={d.icon} size={15} />
                      </span>
                      <span className="arc-sr-only">{d.label}</span>
                    </td>
                    <td className="arc-mono">{c.from_number ?? '—'}</td>
                    <td className="arc-mono">{c.to_number ?? c.destination_e164 ?? '—'}</td>
                    <td className="arc-td-right arc-num">
                      {c.answered_at ? formatDuration(c.duration_sec) : <span className="arc-subtle">no answer</span>}
                    </td>
                    <td>{c.mos ? <MosIndicator mos={c.mos} /> : <span className="arc-subtle">—</span>}</td>
                    <td className="arc-td-right arc-num">{formatMoney(c.sell_amount, c.currency)}</td>
                    <td title={!c.has_recording ? undefined
                      : c.recording_status === 'saving' ? 'Recorded — still being saved to storage'
                      : c.recording_status === 'unavailable' ? 'Recorded — the file is not in storage'
                      : 'Recorded — open the call to play it'}>
                      {c.has_recording && (
                        <span className="arc-subtle" style={{ display: 'inline-flex' }}>
                          <Icon name="play" size={13} />
                          <span className="arc-sr-only">Recorded</span>
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr id={`call-${c.id}`}>
                      <td colSpan={9} style={{ padding: 16, background: 'var(--arc-surface-2)' }}>
                        <CallDetail tenantId={tenantId} callId={c.id} tz={tz} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {!isLoading && data?.data.length === 0 && (
          <Empty
            icon="phone"
            title="No calls yet"
            hint="Call records appear here once the switch ships them. Dial 9196 from a registered handset to generate one."
          />
        )}
      </div>
    </>
  );
}

/** MOS with a label as well as a colour, so it does not rely on hue alone. */
function MosIndicator({ mos }: { mos: string }) {
  const v = Number(mos);
  const tone = v >= 4.0 ? 'ok' : v >= 3.5 ? 'warn' : 'danger';
  const label = v >= 4.0 ? 'good' : v >= 3.5 ? 'fair' : 'poor';
  return (
    <Badge tone={tone}>
      <span className="arc-dot" aria-hidden="true" />
      {mos} {label}
    </Badge>
  );
}
